import { DecimalPipe, NgClass } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { OverlayLoaderDirective } from 'app/core/directives/overlay-loader.directive';
import { CompanyDatePipe } from 'app/core/pipes/company-date.pipe';
import { ShortNumberPipe } from 'app/core/pipes/short-number.pipe';
import { finalize } from 'rxjs/operators';
import { ScbApiService } from '../scb-api.service';
import {
  canPayMyBillingInvoice,
  canSubmitMyBillingProof,
  extractScbData,
  tenantPaymentStatusLabel,
  tenantPaymentStatusTone,
} from '../scb-ui.utils';
import {
  ScbMyBillingInvoice,
  ScbMyBillingPendingInvoices,
  ScbMyBillingSummary,
  ScbMyBillingTenantPaymentFlags,
} from '../scb.models';
import { ScbPayInvoiceDialogComponent } from './scb-pay-invoice-dialog.component';
import { ScbSubmitPaymentProofDialogComponent } from './scb-submit-payment-proof-dialog.component';
import { ScbTenantInvoicePdfDialogComponent } from './scb-tenant-invoice-pdf-dialog.component';

@Component({
  selector: 'app-scb-my-billing-dialog',
  standalone: true,
  imports: [
    NgClass,
    DecimalPipe,
    MatButtonModule,
    MatDialogActions,
    MatDialogContent,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    CompanyDatePipe,
    ShortNumberPipe,
    OverlayLoaderDirective,
  ],
  templateUrl: './scb-my-billing-dialog.component.html',
  styleUrl: './scb-my-billing-dialog.component.scss',
})
export class ScbMyBillingDialogComponent implements OnInit {
  private readonly api = inject(ScbApiService);
  private readonly dialog = inject(MatDialog);
  private readonly dialogRef = inject(MatDialogRef<ScbMyBillingDialogComponent>);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly summary = signal<ScbMyBillingSummary | null>(null);
  readonly invoices = signal<ScbMyBillingInvoice[]>([]);

  selectedInvoice: ScbMyBillingInvoice | null = null;

  ngOnInit(): void {
    this.load();
  }

  close(): void {
    this.dialogRef.close();
  }

  openInvoiceActionsMenu(inv: ScbMyBillingInvoice): void {
    this.selectedInvoice = inv;
  }

  selectedCanPay(): boolean {
    return canPayMyBillingInvoice(this.selectedInvoice);
  }

  selectedCanSubmitProof(): boolean {
    return canSubmitMyBillingProof(this.selectedInvoice);
  }

  paymentStatusLabel(row: ScbMyBillingTenantPaymentFlags): string {
    return tenantPaymentStatusLabel(row);
  }

  paymentStatusTone(row: ScbMyBillingTenantPaymentFlags): 'muted' | 'warn' | 'info' {
    return tenantPaymentStatusTone(row);
  }

  viewSelectedInvoice(): void {
    this.viewInvoice(this.selectedInvoice?.salesInvoiceId, this.selectedInvoice?.invoiceNumber);
  }

  paySelectedInvoice(): void {
    const inv = this.selectedInvoice;
    if (!inv?.salesInvoiceId || !this.selectedCanPay()) return;

    const ref = this.dialog.open(ScbPayInvoiceDialogComponent, {
      width: '640px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      autoFocus: false,
      data: {
        salesInvoiceId: inv.salesInvoiceId,
        invoiceNumber: inv.invoiceNumber ?? null,
        amount: inv.amount ?? null,
        remainingAmount: inv.remainingAmount ?? null,
      },
      panelClass: 'scb-pay-invoice-dialog-panel',
    });

    ref.afterClosed().subscribe((paid) => {
      if (paid) this.load();
    });
  }

  submitProofSelectedInvoice(): void {
    const inv = this.selectedInvoice;
    if (!inv?.salesInvoiceId || !this.selectedCanSubmitProof()) return;

    const ref = this.dialog.open(ScbSubmitPaymentProofDialogComponent, {
      width: '560px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      autoFocus: false,
      data: {
        salesInvoiceId: inv.salesInvoiceId,
        invoiceNumber: inv.invoiceNumber ?? null,
        amount: inv.amount ?? null,
        remainingAmount: inv.remainingAmount ?? null,
      },
      panelClass: 'scb-submit-payment-proof-dialog-panel',
    });

    ref.afterClosed().subscribe((submitted) => {
      if (submitted) this.load();
    });
  }

  viewInvoice(salesInvoiceId: string | null | undefined, titleHint?: string | null): void {
    const id = salesInvoiceId?.trim();
    if (!id) return;
    this.dialog.open(ScbTenantInvoicePdfDialogComponent, {
      width: '960px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      autoFocus: false,
      data: { salesInvoiceId: id, titleHint: titleHint ?? null },
      panelClass: 'scb-tenant-invoice-pdf-dialog-panel',
    });
  }

  fullPkr(value: number | null | undefined): string {
    return `PKR ${(value ?? 0).toLocaleString()}`;
  }

  invoiceTitle(inv: ScbMyBillingInvoice): string {
    if (inv.invoiceNumber?.trim()) return inv.invoiceNumber.trim();
    if (inv.periodLabel?.trim()) return inv.periodLabel.trim();
    if (inv.salesInvoiceId) {
      return `Invoice ${inv.salesInvoiceId.slice(0, 8).toUpperCase()}`;
    }
    return 'Invoice';
  }

  private load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api
      .getMyBillingPendingInvoices()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (resp) => {
          const data = extractScbData<ScbMyBillingPendingInvoices>(resp);
          if (!data) {
            this.error.set(resp?.message || 'Unable to load pending invoices');
            return;
          }
          this.summary.set(data.summary ?? null);
          this.invoices.set(Array.isArray(data.invoices) ? data.invoices : []);
        },
        error: (err: { error?: { message?: string } }) => {
          this.error.set(err?.error?.message || 'Unable to load pending invoices');
        },
      });
  }
}
